import { HomeRoutes } from "@vana/home";

/** App-shell level routes (auth, role landings). Feature routes live in their
 *  own packages (e.g. HomeRoutes). */
export const AppRoutes = {
  ROOT: "/",
  LOGIN: "/login",
  PENDING: "/pending",
  /** Ambassador landing ("Ruta del día"). */
  AMBASSADOR_ROUTE: "/ruta",
  /** One delivery's detail ("Detalle de la entrega"). */
  AMBASSADOR_DELIVERY: "/ruta/:requestId",
  /** KYC checklist ("Verificar identidad") for a delivery. */
  AMBASSADOR_KYC: "/ruta/:requestId/kyc",
  /** POD checklist ("Registrar entrega") for a delivery. */
  AMBASSADOR_POD: "/ruta/:requestId/pod",
  /** Financial-education checklist ("Educación financiera") for a delivery. */
  AMBASSADOR_EDUCATION: "/ruta/:requestId/education",
  /** First-purchase step ("Primera compra") for a delivery. */
  AMBASSADOR_FIRST_PURCHASE: "/ruta/:requestId/first-purchase",
  /** Referrals capture ("Referidos") for a delivery. */
  AMBASSADOR_REFERRALS: "/ruta/:requestId/referrals",
  /** Failed-delivery report ("No pude entregar") for a delivery. */
  AMBASSADOR_FAILED_DELIVERY: "/ruta/:requestId/failed-delivery",
  /** Deferred problem report ("Reportar un problema") for a delivery. */
  AMBASSADOR_PROBLEM_REPORT: "/ruta/:requestId/reportar",
  /** End-of-day close ("Cierre de jornada"). */
  AMBASSADOR_DAY_CLOSE: "/ruta/cierre",
  /** Single capture screen ("Tomar foto") — shared by KYC and POD. */
  AMBASSADOR_CAPTURE: "/ruta/:requestId/capture/:captureType",

  /** Supervisor day dashboard ("Resumen del día", E3.1) — the supervisor landing. */
  SUPERVISOR_SUMMARY: "/backoffice/resumen",
  /** Backoffice route-assignment for the supervisor ("Asignación de rutas"). */
  SUPERVISOR_ROUTE_ASSIGNMENT: "/backoffice/asignacion-de-rutas",
  /** Supervisor report queue ("Reportes", E3.3). */
  SUPERVISOR_REPORTS: "/backoffice/reportes",
  /** Supervisor reschedule queue ("Reagendamientos", E3.4). */
  SUPERVISOR_RESCHEDULES: "/backoffice/reagendamientos",
  /** All deliveries list ("Todas las entregas") — shared by supervisor (team)
   *  and admin (whole operation). Parent of SUPERVISOR_DELIVERY. */
  ALL_DELIVERIES: "/backoffice/entregas",
  /** Supervisor/admin delivery detail ("Detalle de la entrega", E3.5). */
  SUPERVISOR_DELIVERY: "/backoffice/entregas/:requestId",
  /** Supervisor team roster ("Mi equipo", E3.6). */
  SUPERVISOR_TEAM: "/backoffice/mi-equipo",
  // Shared by supervisor and admin: the same screen renders each role's own
  // grouping (own ambassadors vs. every supervisor), so one path serves both.
  REFERRALS_BACKOFFICE: "/backoffice/referidos",
  /** Contact Center "Por confirmar" queue (VCE-664) — shared with the supervisor. */
  PENDING_CONFIRMATION: "/backoffice/por-confirmar",
  /** "No entregadas" (DMS-35) — supervisor, Contact Center, Operaciones, Admin. */
  UNDELIVERED: "/backoffice/no-entregadas",
  /** One ambassador's deliveries for the day ("Entregas del embajador", E3.6). */
  SUPERVISOR_AMBASSADOR_DELIVERIES: "/backoffice/mi-equipo/:ambassadorId/entregas",
  /** Backoffice landing for the admin ("Usuarios y roles"). */
  ADMIN_USERS: "/backoffice/usuarios",
  /** Admin coverage screen ("Zonas"). */
  ADMIN_ZONES: "/backoffice/zonas",
  /** Operations batch pipeline ("Lotes de tarjetas", E4.1). */
  OPERATIONS_BATCHES: "/backoffice/lotes",
  /** Operations card inventory ("Inventario de tarjetas", E4.2). */
  OPERATIONS_INVENTORY: "/backoffice/inventario",
  /** Operations distribution board ("Distribución", E4.3). */
  OPERATIONS_DISTRIBUTION: "/backoffice/distribucion",
  /** Operations day close / reception ("Cierre del día", E4.4). */
  OPERATIONS_DAY_CLOSE: "/backoffice/cierre",
  /** Operations document custody ("Custodia documental", E4.5). */
  OPERATIONS_CUSTODY: "/backoffice/custodia",
  /** Compliance landing ("Clientes", DMS-27) — shared by compliance, admin and
   *  operations; read-only. */
  COMPLIANCE_CUSTOMERS: "/backoffice/compliance/clientes",
  /** One delivery's case for Compliance ("Caso del cliente", DMS-27). */
  COMPLIANCE_CASE: "/backoffice/compliance/clientes/:customerId/entregas/:requestId",
};

/** Builds the path to a Compliance case (one delivery of one customer). */
export const complianceCasePath = (customerId: string, requestId: string) =>
  `/backoffice/compliance/clientes/${customerId}/entregas/${requestId}`;

/** Builds the path to a delivery's detail screen. */
export const deliveryPath = (requestId: string) => `/ruta/${requestId}`;

/** Builds the path to the supervisor's delivery detail (E3.5). */
export const supervisorDeliveryPath = (requestId: string) => `/backoffice/entregas/${requestId}`;

/** Builds the path to one ambassador's deliveries screen (E3.6). */
export const ambassadorDeliveriesPath = (ambassadorId: string) =>
  `/backoffice/mi-equipo/${ambassadorId}/entregas`;

/** Builds the path to a delivery's KYC checklist. */
export const kycPath = (requestId: string) => `/ruta/${requestId}/kyc`;

/** Builds the path to a delivery's POD ("Registrar entrega") checklist. */
export const podPath = (requestId: string) => `/ruta/${requestId}/pod`;

/** Builds the path to a delivery's "Educación financiera" checklist. */
export const educationPath = (requestId: string) => `/ruta/${requestId}/education`;

/** Builds the path to a delivery's "Primera compra" screen. */
export const firstPurchasePath = (requestId: string) => `/ruta/${requestId}/first-purchase`;

/** Builds the path to a delivery's "Referidos" screen. */
export const referralsPath = (requestId: string) => `/ruta/${requestId}/referrals`;

/** Builds the path to a delivery's "No pude entregar" screen. */
export const failedDeliveryPath = (requestId: string) => `/ruta/${requestId}/failed-delivery`;

/** Builds the path to a delivery's "Reportar un problema" screen. */
export const problemReportPath = (requestId: string) => `/ruta/${requestId}/reportar`;

/** Path to the "Cierre de jornada" (end-of-day close) screen. */
export const dayClosePath = () => `/ruta/cierre`;

/** Builds the path to a single capture screen (KYC or POD). */
export const capturePath = (requestId: string, captureType: string) =>
  `/ruta/${requestId}/capture/${captureType}`;

export const routes = {
  home: HomeRoutes,
};
